/**
 * Object API Name Normalizer
 *
 * Converts object API names to Gobodo format (with configurable __c suffix for custom objects).
 * Handles the mismatch between app usage ('Client') and Gobodo storage ('Client__c').
 * Suffix is configurable via REACT_APP_GOBODO_CUSTOM_SUFFIX (default __c).
 */

/** Custom object suffix for Gobodo; default __c, overridable via env */
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const LEGACY_SUFFIX = '__c';

/**
 * Map of standard object names to their Gobodo API names
 * Standard objects that don't use the custom suffix in Gobodo
 */
const STANDARD_OBJECT_MAP: Record<string, string> = {
  'User': 'User',
};

/**
 * Base names of custom objects (logical names). Gobodo API names are derived as base + CUSTOM_SUFFIX.
 */
const CUSTOM_OBJECT_BASES = [
  'Client', 'Lead', 'Contact', 'Opportunity', 'Account', 'Contract',
  'Task', 'Event', 'Case', 'Note', 'Report', 'Pricebook', 'Product', 'CreditReport',
  'CreditBlockBureau', 'CreditBlockHistory', 'Credit_Bureaus', 'Unlock_Request',
  'EmailTemplate', 'CyberSweepMeetingAgenda',
] as const;

/** Build custom object map from suffix so one env var controls all custom object names */
function buildCustomObjectMap(): Record<string, string> {
  const map: Record<string, string> = {};
  CUSTOM_OBJECT_BASES.forEach(base => {
    map[base] = base + CUSTOM_SUFFIX;
  });
  return map;
}

const CUSTOM_OBJECT_MAP = buildCustomObjectMap();

/**
 * Capitalize the first letter of a string
 */
function capitalizeFirst(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Normalize object API name to Gobodo format
 * 
 * Converts app-friendly names (e.g., 'Client', 'client__c') to Gobodo format (e.g., 'Client__c')
 * Handles both case normalization and __c suffix.
 * 
 * @param objectApiName - Object API name from app (e.g., 'Client', 'client__c', 'CLIENT')
 * @returns Normalized object API name for Gobodo (e.g., 'Client__c')
 * 
 * @example
 * normalizeToGobodoFormat('Client') // Returns 'Client__c'
 * normalizeToGobodoFormat('client__c') // Returns 'Client__c'
 * normalizeToGobodoFormat('CLIENT__C') // Returns 'Client__c'
 * normalizeToGobodoFormat('User') // Returns 'User' (standard object)
 */
export function normalizeToGobodoFormat(objectApiName: string): string {
  if (!objectApiName) {
    return objectApiName;
  }

  let trimmed = objectApiName.trim();
  const suffixLower = CUSTOM_SUFFIX.toLowerCase();
  const hasCustomSuffix = trimmed.toLowerCase().endsWith(suffixLower);

  if (hasCustomSuffix && CUSTOM_SUFFIX !== LEGACY_SUFFIX) {
    const beforeSuffix = trimmed.slice(0, -CUSTOM_SUFFIX.length);
    if (beforeSuffix.toLowerCase().endsWith(LEGACY_SUFFIX)) {
      trimmed = beforeSuffix.slice(0, -LEGACY_SUFFIX.length) + CUSTOM_SUFFIX;
    }
  }

  if (!hasCustomSuffix && CUSTOM_SUFFIX !== LEGACY_SUFFIX && trimmed.toLowerCase().endsWith(LEGACY_SUFFIX)) {
    trimmed = trimmed.slice(0, -LEGACY_SUFFIX.length);
  }

  const baseNameNoSuffix = hasCustomSuffix ? trimmed.slice(0, -CUSTOM_SUFFIX.length) : trimmed;
  const baseCapitalized = capitalizeFirst(baseNameNoSuffix.toLowerCase());

  if (STANDARD_OBJECT_MAP[baseCapitalized]) {
    return STANDARD_OBJECT_MAP[baseCapitalized];
  }
  if (CUSTOM_OBJECT_MAP[baseCapitalized]) {
    return CUSTOM_OBJECT_MAP[baseCapitalized];
  }

  // Unknown custom object: preserve original case so backend key matches (e.g. test_feb_5__c)
  if (hasCustomSuffix) {
    return trimmed;
  }
  // Preserve literal __stg type names so we don't double-append suffix (e.g. Type__stg -> Type__stg, not Type__stg__dev_c)
  if (trimmed.toLowerCase().endsWith('__stg')) {
    return trimmed;
  }
  return `${trimmed}${CUSTOM_SUFFIX}`;
}

/**
 * Convert Gobodo format back to app format (removes __c if present)
 * 
 * @param gobodoApiName - Object API name from Gobodo (e.g., 'Client__c')
 * @returns App-friendly object API name (e.g., 'Client')
 * 
 * @example
 * normalizeFromGobodoFormat('Client__c') // Returns 'Client'
 * normalizeFromGobodoFormat('User') // Returns 'User'
 */
export function normalizeFromGobodoFormat(gobodoApiName: string): string {
  if (!gobodoApiName) {
    return gobodoApiName;
  }

  // Remove custom suffix if present
  if (gobodoApiName.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) {
    return gobodoApiName.slice(0, -CUSTOM_SUFFIX.length);
  }

  return gobodoApiName;
}
